import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES, getCurrencySymbol, formatCompactNumber } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';
import { format, parseISO } from 'date-fns';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Cell,
} from 'recharts';

export const ExpenseAnomalyDetectorChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance, currencySettings } = useBoardChartData();

    const currencySymbol = useMemo(
        () => getCurrencySymbol(currencySettings?.default),
        [currencySettings?.default]
    );

    const chartData = useMemo(() => {
        if (!finance?.dailyBreakdown) return [];

        const sortedDates = Object.keys(finance.dailyBreakdown!).sort();

        const dailyExpenses = sortedDates.map((date) => {
            const metrics = finance.dailyBreakdown![date]?.metrics || {};
            return {
                date,
                formattedDate: format(parseISO(date), 'MMM dd'),
                expenses: Math.abs(Number(metrics.expenses) || 0),
            };
        });

        if (dailyExpenses.length === 0) return [];

        const values = dailyExpenses.map((d) => d.expenses);
        const mean = values.reduce((sum, v) => sum + v, 0) / values.length;
        const variance = values.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / values.length;
        const stddev = Math.sqrt(variance);
        const threshold = mean + 2 * stddev;

        return dailyExpenses.map((d) => ({
            ...d,
            isAnomaly: d.expenses > threshold,
        }));
    }, [finance?.dailyBreakdown]);

    const isEmpty = useMemo(() => chartData.length === 0, [chartData]);

    const anomalyCount = useMemo(
        () => chartData.filter((d) => d.isAnomaly).length,
        [chartData]
    );

    const summary = useMemo(
        () => (
            <span className="text-[10px] text-text-primary">
                {anomalyCount > 0
                    ? t('charts.anomaliesDetected', '{{count}} anomalies detected', { count: anomalyCount })
                    : t('charts.noAnomalies', 'No anomalies detected')}
            </span>
        ),
        [anomalyCount, t]
    );

    useSetChartMetadata({ isEmpty, summary });

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={chartData}
                        margin={CHART_STYLES.margin.withAxis}
                    >
                        <CartesianGrid
                            vertical={false}
                            stroke={CHART_STYLES.grid.stroke}
                            strokeDasharray={CHART_STYLES.grid.strokeDasharray}
                            strokeWidth={CHART_STYLES.grid.strokeWidth}
                            opacity={CHART_STYLES.grid.opacity}
                        />
                        <XAxis
                            dataKey="formattedDate"
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            tickMargin={CHART_STYLES.axis.tickMargin}
                        />
                        <YAxis
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            tickFormatter={(value: number) => formatCompactNumber(value, currencySymbol)}
                        />
                        <Tooltip
                            contentStyle={CHART_STYLES.tooltip.contentStyle}
                            cursor={CHART_STYLES.tooltip.cursor}
                            formatter={(value: number, _name: string, props: any) => {
                                const payload = props.payload as { isAnomaly?: boolean } | undefined;
                                const label = payload?.isAnomaly
                                    ? t('charts.anomalyExpense', 'Expense (Anomaly)')
                                    : t('charts.expense', 'Expense');
                                return [formatCompactNumber(value, currencySymbol), label];
                            }}
                            labelFormatter={(label: string) => label}
                        />
                        <Bar
                            dataKey="expenses"
                            radius={CHART_STYLES.bar.radius}
                            maxBarSize={CHART_STYLES.bar.maxBarSize}
                        >
                            {chartData.map((entry, index) => (
                                <Cell
                                    key={`cell-${index}`}
                                    fill={entry.isAnomaly ? CHART_COLORS.danger : CHART_COLORS.gray[300]}
                                />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
